import { useTranslation } from 'react-i18next';

import '@/components/tavolo/tavolo.css';
import { cn } from '@/lib/cn';
import type { Board, LineResult, Row } from '@/lib/qcp/protocol';
import { OfcRow } from './OfcRow';
import { rowToLabel } from './rowUtils';
import type { TagliaCp } from './cartaCp';

/** Per ogni fila, cio' che il RESOCONTO scrive a destra (v. `OfcRow`). */
export type InfoFila = Record<Row, { esito: LineResult | null; royalty: number }>;

interface OfcBoardProps {
  board: Board;
  capacities?: { front: 3; middle: 5; back: 5 };
  /**
   * Called when a slot is tapped: `(row, slotIndex)`. Enables both placing
   * (tap an empty slot / a row with a card selected) and un-placing (tap a
   * pending card to send it back to the tray). Per-slot so the caller can tell
   * which card was tapped.
   */
  onSlotTap?: (row: Row, index: number) => void;
  /** Keys (`${r}${s}`) of cards placed THIS hand — dim ring. */
  pendingKeys?: Set<string>;
  /** Key of the picked-up pending card — bright ring (selected for move/remove). */
  pickedKey?: string;
  /** Rows that should glow as legal placement targets (the «invito»). */
  highlightRows?: Row[];
  /** La fila sotto il dito mentre si trascina una carta della mano (solo se e un bersaglio legale, cioe in `highlightRows`). */
  filaSopra?: Row | null;
  /**
   * The opponent's board: smaller cards, no slot-tap interactions (the server already masks the opponent's private state; compact is a layout hint).
   * ⇒ taglia `avv` (prima era una scala 0,82 sul contenitore: ora le carte hanno la loro misura).
   */
  compact?: boolean;
  /** La taglia delle carte; senza, `avv` se `compact`, altrimenti `mia`. */
  taglia?: TagliaCp;
  quattroColori?: boolean;
  /** Il RESOCONTO: esito e royalty di ogni fila. */
  info?: InfoFila;
  /** Le file di chi ha fatto foul: sbiadite. */
  sbiadita?: boolean;
  /** Le file con ancora spazio portano un filo d'oro tenue (il mio tavolo mentre si gioca). */
  filoSulleLibere?: boolean;
}

/**
 * Full OFC board — three rows stacked front (3) → middle (5) → back (5), in a panel of dark glass with the gold thread.
 *
 * In normal mode: slots are tappable (onSlotTap fires per slot) and the «invito» runs along the rows in `highlightRows`.
 * In `compact` mode (opponent board): no tap handlers, smaller cards.
 */
export function OfcBoard({
  board,
  capacities = { front: 3, middle: 5, back: 5 },
  onSlotTap,
  pendingKeys,
  pickedKey,
  highlightRows = [],
  filaSopra = null,
  compact = false,
  taglia,
  quattroColori = false,
  info,
  sbiadita = false,
  filoSulleLibere = false,
}: OfcBoardProps) {
  const { t } = useTranslation('chinesepoker');
  const misura: TagliaCp = taglia ?? (compact ? 'avv' : 'mia');
  const rows: { row: Row; cards: typeof board.front; capacity: 3 | 5 }[] = [
    { row: 'front', cards: board.front, capacity: capacities.front },
    { row: 'middle', cards: board.middle, capacity: capacities.middle },
    { row: 'back', cards: board.back, capacity: capacities.back },
  ];

  return (
    <div data-ofc-board className={cn('tav-vetro tav-rilievo flex flex-col rounded-2xl', misura === 'mini' || misura === 'mia' ? 'gap-0.5 p-1' : misura === 'avv' ? 'gap-0.5 p-1.5' : 'gap-1 p-2')}>
      {rows.map(({ row, cards, capacity }) => (
        <OfcRow
          key={row}
          label={rowToLabel(row)}
          cards={cards}
          capacity={capacity}
          taglia={misura}
          quattroColori={quattroColori}
          highlight={highlightRows.includes(row) && !compact}
          sopra={!compact && filaSopra === row && highlightRows.includes(row)}
          contorno={filoSulleLibere && cards.length < capacity ? 'filo' : 'nessuno'}
          onSlotTap={!compact && onSlotTap ? (idx) => onSlotTap(row, idx) : undefined}
          pendingKeys={compact ? undefined : pendingKeys}
          pickedKey={compact ? undefined : pickedKey}
          conResoconto={info !== undefined}
          esito={info?.[row].esito}
          royalty={info?.[row].royalty}
          etichettaRoyalty={info ? t('resoconto.royalty') : undefined}
          sbiadita={sbiadita}
        />
      ))}
    </div>
  );
}
